<template>
  <!-- Invoice Details Modal -->
  <b-modal
    :visible="show"
    title="Invoice Details"
    centered
    @hide="$emit('close')"
  >
    <div v-if="logDetails">
      <div class="mb-2">
        <strong>Invoice Number : </strong> {{ logDetails.invoice_number }}
      </div>
      <div class="mb-2">
        <strong>Purchase Bill Number : </strong> {{ logDetails.purchase_bill_number }}
      </div>
      <div class="mb-2">
        <strong>Subject : </strong> {{ logDetails.subject }}
      </div>
      <div class="mb-2">
        <strong>Email From : </strong> {{ logDetails.email_from }}
      </div>
      <div class="mb-2">
        <strong>Status : </strong> {{ logDetails.status }}
      </div>
      <div class="mb-3">
        <strong>Remarks : </strong>
        <div v-if="logDetails.remarks">
          <div
            v-for="(remark, index) in parseRemarks(logDetails.remarks)"
            :key="index"
            class="mb-2"
          >
            <!-- Handle invoice existence message -->
            <div
              v-if="remark.type === 'text'"
              class="alert alert-warning px-2 py-1"
            >
              {{ remark.content }}
            </div>

            <!-- Handle products not found message -->
            <div
              v-if="remark.type === 'products'"
              class="alert alert-warning px-2 py-1"
            >
              <div class="font-weight-bold">
                {{ remark.summary }}
              </div>
              <div class="mt-1">
                Products not found :
              </div>
              <ul class="missing-products-list pl-3 mt-1 mb-0">
                <li
                  v-for="(product, idx) in remark.products"
                  :key="idx"
                >
                  {{ product }}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div v-else>
          No remarks available
        </div>
      </div>
      <div class="mb-2">
        <strong>Created At : </strong> {{ logDetails.created_at && formatedDate(logDetails.created_at,'DD-MM-YYYY LT') }}
      </div>
    </div>
    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="warning"
        :disabled="reProcessing"
        @click="reProcessEmail"
      >
        Re-Process
        <feather-icon
          v-b-tooltip.hover.top="'Most recent email with same subject will be marked as unerad'"
          icon="InfoIcon"
          size="16"
          class="cursor-pointer"
        />
        <b-spinner
          v-if="reProcessing"
          small
          class="ml-1"
        />
      </b-button>
      <b-button
        variant="primary"
        :disabled="resolving"
        @click="markResolved"
      >
        Mark Resolved
        <b-spinner
          v-if="resolving"
          small
          class="ml-1"
        />
      </b-button>
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>

    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import dateMixin from '@/mixins/dateMixin'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BModal, BButton, BSpinner,
  },
  mixins: [dateMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    logDetails: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      reProcessing: false,
      resolving: false,
    }
  },
  methods: {
    reProcessEmail() {
      this.reProcessing = true

      axios
        .post('re_process_purchase_email', {
          id: this.logDetails.id,
        })
        .then(res => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.data?.details || 'Email set for re-process',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.reProcessing = false
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error?.response?.data?.details || 'Request Failed',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          this.reProcessing = false
        })
    },
    markResolved() {
      this.resolving = true

      axios
        .post('resolve_po_email_log', {
          id: this.logDetails.id,
        })
        .then(res => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.data?.details || 'Log marked as Resolved',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.resolving = false

          this.$emit('resolved')
          this.$emit('close')
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error?.response?.data?.details || 'Request Failed',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          this.resolving = false
        })
    },
    parseRemarks(remarks) {
      if (!remarks) return []

      // Split the remarks by the delimiter
      const remarkParts = remarks.split('--')
      const result = []

      // Process each part
      remarkParts.forEach(part => {
        const part1 = part.trim()

        if (part1.includes('products not found in database')) {
          // Handle product not found message
          const totalMatch = part.match(/Total: (\d+)/)
          const productsMatch = part.match(/\[(.*?)\]/)

          if (totalMatch && productsMatch) {
            result.push({
              type: 'products',
              summary: `Total: ${totalMatch[1]} products not found in database`,
              products: productsMatch[1].split(',').map(item => item.replace(/'/g, '').trim()),
            })
          } else {
            // If it doesn't match the expected format, treat as text
            result.push({
              type: 'text',
              content: part1,
            })
          }
        } else {
          // Handle other messages (like invoice doesn't exist)
          result.push({
            type: 'text',
            content: part,
          })
        }
      })

      return result
    },
    // Keep these methods for backward compatibility
    getRemarksSummary(remarks) {
      if (!remarks) return ''

      // Extract the total count part from the remarks
      const totalMatch = remarks.match(/Total: (\d+)/)
      if (totalMatch && totalMatch[1]) {
        return `Total: ${totalMatch[1]} products not found in database`
      }

      return remarks
    },
    getProductsList(remarks) {
      if (!remarks) return []

      // Try to extract the array part from the string
      const productsMatch = remarks.match(/\[(.*?)\]/)
      if (productsMatch && productsMatch[1]) {
        // Split by comma and remove quotes
        return productsMatch[1].split(',').map(item => item.replace(/'/g, '').trim())
      }

      return []
    },
  },
}
</script>

<style lang="scss" scoped>
.missing-products-list {
  list-style-type: disc;

  li {
    font-family: monospace;
    font-size: 0.9rem;
    color: #7367f0;
    padding: 0.25rem 0.5rem;
    border-radius: 3px;
  }
}
</style>
